// src/pages/outsourcing_management/constants/inquiryStatusColors.ts
//
// Status colours and display labels for the partner Inquiry workflow
// (EE-253) - InquiryStatusEnum. Mirrors requisitionStatusColors.ts.
import { InquiryStatusEnum, InquiryTypeEnum } from "types/InquiryTypes";
import {
  STATUS_PALETTE,
  StatusColors,
} from "components/Dashboard/StatusFilterChips";

export const INQUIRY_STATUS_COLORS: Record<InquiryStatusEnum, StatusColors> = {
  [InquiryStatusEnum.DRAFT]: STATUS_PALETTE.DRAFT,
  [InquiryStatusEnum.APPROVED]: STATUS_PALETTE.APPROVED,
  [InquiryStatusEnum.AWAITING_RESPONSE]: {
    border: "1px solid #007474",
    color: "#007474",
    background: "#e6f1f1",
  },
  // The purchase order has gone out but nobody has confirmed it - the
  // design guide's call-to-action yellow, because it is waiting on the
  // supplier.
  [InquiryStatusEnum.PO_SENT]: {
    border: "1px solid #e1a900",
    color: "#e1a900",
    background: "#fcf6e5",
  },
  [InquiryStatusEnum.ORDERED]: STATUS_PALETTE.ORDERED,
  [InquiryStatusEnum.PICKUP_DELIVERY]: STATUS_PALETTE.PICKUP_DELIVERY,
  [InquiryStatusEnum.READY_TO_USE]: STATUS_PALETTE.READY_TO_USE,
  [InquiryStatusEnum.CLOSED]: STATUS_PALETTE.CLOSED,
};

export const INQUIRY_STATUS_LABELS: Record<InquiryStatusEnum, string> = {
  [InquiryStatusEnum.DRAFT]: "Draft",
  [InquiryStatusEnum.APPROVED]: "Approved",
  // Purchasing calls this stage WIP, and the requisition side already says
  // so (requisitionStatusColors.ts). The stored enum stays
  // AWAITING_RESPONSE - the value is an identifier, and renaming it would
  // mean migrating REQUISITIONS.state plus every historic audit string for a
  // word.
  [InquiryStatusEnum.AWAITING_RESPONSE]: "WIP",
  [InquiryStatusEnum.PO_SENT]: "PO Sent",
  [InquiryStatusEnum.ORDERED]: "Ordered",
  [InquiryStatusEnum.PICKUP_DELIVERY]: "Pickup / Delivery",
  [InquiryStatusEnum.READY_TO_USE]: "Ready to Use",
  [InquiryStatusEnum.CLOSED]: "Closed",
};

// Falls back to the raw value so a status written before EE-253 (REQUEST,
// PROCESSING, ...) still renders as something rather than blank.
export const inquiryStatusLabel = (status: string | null | undefined): string =>
  status ? INQUIRY_STATUS_LABELS[status as InquiryStatusEnum] ?? status : "";

// The Inquiry flow, one step at a time - mirrors ALLOWED_TRANSITIONS in
// backend/common/enums/InquiryStatusEnum.py. Forward one stage, back to any
// earlier stage, and CLOSED from anywhere.
//
// CLOSED is not in the list - it is reachable from anywhere, terminal, and
// handled separately below. The requisitions behind an inquiry finishing close
// it from Ready to Use by themselves; a hand-raised one the buyer closes.
//
// PICKUP_DELIVERY and READY_TO_USE are in the list for ordering and for the
// status token, but the backend reads them off the per-line counts on every
// goods-in save. See isInquiryCountDriven, which is what keeps Ready to Use
// out of the status menu.
export const INQUIRY_FLOW: InquiryStatusEnum[] = [
  InquiryStatusEnum.DRAFT,
  InquiryStatusEnum.APPROVED,
  InquiryStatusEnum.AWAITING_RESPONSE,
  InquiryStatusEnum.PO_SENT,
  InquiryStatusEnum.ORDERED,
  InquiryStatusEnum.PICKUP_DELIVERY,
  InquiryStatusEnum.READY_TO_USE,
];

// The stage the line counts decide. Offering it in the status menu would give
// the same fact two owners - the buyer's click and the counts - and the next
// goods-in save would silently overwrite whichever one the buyer chose.
export const isInquiryCountDriven = (
  status: InquiryStatusEnum | null | undefined
): boolean => status === InquiryStatusEnum.READY_TO_USE;

// Where the buyer's close of a hand-raised inquiry (no requisition behind it)
// is a finish rather than a cancel: the goods are arriving. Mirrors
// HAND_RAISED_FINISH_FROM in backend/controllers/ecommerce/InquiryController.py,
// which decides it.
export const closesAsFinished = (
  status: InquiryStatusEnum | null | undefined,
  handRaised: boolean
): boolean =>
  handRaised &&
  (status === InquiryStatusEnum.PICKUP_DELIVERY ||
    status === InquiryStatusEnum.READY_TO_USE);

// Finished with, whichever way it ended - mirrors TERMINAL_STATUSES in
// backend/common/enums/InquiryStatusEnum.py. Whether it ran its course or was
// cancelled is `cancelled` on the inquiry, not a different state.
export const isInquiryFinished = (
  status: InquiryStatusEnum | null | undefined
): boolean => status === InquiryStatusEnum.CLOSED;

// Whether the inquiry has settled on one supplier. Before the purchase order
// goes out the shortlist is the supplier field - who is being asked - and the
// final supplier is not a fact yet; from PO Sent on it is the only supplier
// that matters and the shortlist is history. It is the order leaving that
// settles it, not the supplier's answer coming back: the others did not get
// an order. CLOSED sits outside the flow, so a closed inquiry goes by whether
// a final supplier was ever chosen.
export const isInquiryOrdered = (inquiry: {
  state: InquiryStatusEnum | null | undefined;
  partnerId?: number | null;
}): boolean => {
  if (inquiry.state === InquiryStatusEnum.CLOSED) return !!inquiry.partnerId;
  const index = INQUIRY_FLOW.indexOf(inquiry.state as InquiryStatusEnum);
  return index >= INQUIRY_FLOW.indexOf(InquiryStatusEnum.PO_SENT);
};

// Goods are arriving, or have: Pickup / Delivery and everything after it,
// including a Closed that ran its course. A cancelled one ended early and has
// no counts to show. Where the per-line goods-in counts are shown and, until
// finished, edited. Mirrors InquiryVM.deliveryProgress.
export const isInquiryInDelivery = (
  status: InquiryStatusEnum | null | undefined,
  cancelled = false
): boolean => {
  if (status === InquiryStatusEnum.CLOSED) return !cancelled;
  const index = INQUIRY_FLOW.indexOf(status as InquiryStatusEnum);
  return index >= INQUIRY_FLOW.indexOf(InquiryStatusEnum.PICKUP_DELIVERY);
};

// What the button that moves an inquiry *to* a status should say. The status
// token keeps the noun ("Approved"); the button takes the verb, because it
// names an action about to be taken rather than a state already reached.
const INQUIRY_ACTION_LABELS: Partial<Record<InquiryStatusEnum, string>> = {
  [InquiryStatusEnum.APPROVED]: "Approve",
  [InquiryStatusEnum.AWAITING_RESPONSE]: "Move to WIP",
  [InquiryStatusEnum.PO_SENT]: "Convert to purchase order",
  // Ordered is the supplier's answer to what we sent, recorded with whatever
  // they came back with - so the button records, it does not order.
  [InquiryStatusEnum.ORDERED]: "Confirm Purchase Order",
  [InquiryStatusEnum.PICKUP_DELIVERY]: "Pickup / Delivery",
  [InquiryStatusEnum.READY_TO_USE]: "Ready to Use",
  [InquiryStatusEnum.CLOSED]: "Close",
};

// Closing says which way an inquiry ends: a hand-raised one whose goods are
// arriving is finished, anything else is a cancel. Mirrors the `cancelled`
// flag move_inquiry sets.
export const closeActionLabel = (finishes: boolean): string =>
  finishes ? "Finish" : "Cancel";

// The label for an inquiry's status token. Finished and cancelled are both
// CLOSED, so a closed one says which way it ended.
export const inquiryDisplayLabel = (inquiry: {
  state: InquiryStatusEnum | string | null | undefined;
  cancelled?: boolean;
}): string =>
  inquiry.state === InquiryStatusEnum.CLOSED
    ? inquiry.cancelled
      ? "Closed (cancelled)"
      : "Closed (finished)"
    : inquiryStatusLabel(inquiry.state);

export const inquiryActionLabel = (status: InquiryStatusEnum): string =>
  INQUIRY_ACTION_LABELS[status] ?? inquiryStatusLabel(status);

// The submit button inside the purchase-order form, which is not the step
// that opened it: "Convert to purchase order" opens the form and this sends
// it; "Confirm Purchase Order" opens it again once the supplier answers and
// this saves what they came back with (Olena's validation, 05.10.26).
export const PURCHASE_ORDER_SUBMIT_LABELS = {
  convert: "Send Purchase Order",
  confirm: "Save Purchase Order",
} as const;

// A step back may not go below Approved - mirrors BACKWARD_FLOOR in
// backend/common/enums/InquiryStatusEnum.py. Draft is where an inquiry is
// still being written; walking a live one back into it would strand the
// requisition approved on its strength.
export const BACKWARD_FLOOR = InquiryStatusEnum.APPROVED;

export const getAllowedInquiryStatuses = (
  current: InquiryStatusEnum | null | undefined
): InquiryStatusEnum[] => {
  // Terminal - nothing moves out of a closed Inquiry.
  if (isInquiryFinished(current)) return [];
  const index = current ? INQUIRY_FLOW.indexOf(current) : -1;
  // An unknown or missing state (legacy rows - the column is nullable) gets
  // the whole flow rather than nothing, so it can always be rescued - bar the
  // stages the counts decide.
  if (index === -1)
    return [
      ...INQUIRY_FLOW.filter((status) => !isInquiryCountDriven(status)),
      InquiryStatusEnum.CLOSED,
    ];
  const floorIndex = INQUIRY_FLOW.indexOf(BACKWARD_FLOOR);
  return [
    ...INQUIRY_FLOW.slice(Math.max(0, floorIndex), index),
    // One step forward. Mirrors _LINEAR_NEXT in
    // backend/common/enums/InquiryStatusEnum.py, which stops at Ready to Use:
    // from there the inquiry only closes.
    ...INQUIRY_FLOW.slice(index + 1, index + 2),
    // The one permitted skip - mirrors _SKIP_FORWARD on the backend, which
    // lands on PO_SENT: skipping the wait for a quote does not skip sending
    // the purchase order or the supplier confirming it.
    ...(current === InquiryStatusEnum.APPROVED
      ? [InquiryStatusEnum.PO_SENT]
      : []),
    InquiryStatusEnum.CLOSED,
    // Ready to Use is read off the line counts on every goods-in save, so
    // offering it here would give the same fact two owners and the next save
    // would overwrite whichever the buyer picked.
  ].filter((status) => !isInquiryCountDriven(status));
};

// An Inquiry is "open" until the goods are on the shelf - mirrors
// OPEN_STATUSES in backend/common/enums/InquiryStatusEnum.py. Used to ask the
// API for the inquiries still worth attaching to a production task.
export const OPEN_INQUIRY_STATUSES: InquiryStatusEnum[] = INQUIRY_FLOW.filter(
  (status) =>
    status !== InquiryStatusEnum.READY_TO_USE
);

// Display labels for the Inquiry type. The dropdown and the row captions used
// to print the raw enum value, so an Inquiry read "CNC" no matter what the
// requisition type it came from was called.
//
// Kept in step with the REQUISITION_TYPES labels, which is where these names
// are decided - the codes are deliberately the same on both sides.
export const INQUIRY_TYPE_LABELS: Record<InquiryTypeEnum, string> = {
  [InquiryTypeEnum.CNC]: "CNC",
  [InquiryTypeEnum.PRINT3D]: "3D Print",
  [InquiryTypeEnum.SHEET]: "Sheet",
  [InquiryTypeEnum.FINISH]: "Finishing",
  [InquiryTypeEnum.MATERIAL]: "Raw Material",
  [InquiryTypeEnum.PROCESSED_RAW_MATERIAL]: "Processed Raw Material",
  [InquiryTypeEnum.CUSTOM]: "Custom",
  [InquiryTypeEnum.TOOL]: "Tools & Equipment",
};

// Falls back to the raw value so a type this table has not caught up with
// still renders as something rather than blank.
export const inquiryTypeLabel = (type: string | null | undefined): string =>
  type ? INQUIRY_TYPE_LABELS[type as InquiryTypeEnum] ?? type : "";

// The goods-in stages as the delivery bars draw them, in the order a piece
// travels (EE-253): announced by the supplier, here, checked over. Shared by
// the dialog's DeliveryHeadline and the board row's DeliveryStatus so the two
// bars never disagree.
export const GOODS_IN_COLORS = {
  readyToPickUp: "warning.main",
  received: "#66CDAA", // aquamarine
  readyToUse: "success.dark",
};
